import React, { useEffect, useRef } from "react";
import { selectDeviceVariant } from "./adVariant";
import { REFRESH_RSS_URL } from "../constants";

const RSS_TIMEOUT = 3000;

const ENTITIES = {
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&quot;": '"',
    "&apos;": "'",
    "&#39;": "'",
};

const ITEM_RE = /<item\b[^>]*>([\s\S]*?)<\/item>/i;
const LINK_RE = /<link\b[^>]*>([\s\S]*?)<\/link>/i;
const CDATA_RE = /^<!\[CDATA\[([\s\S]*)\]\]>$/;

// Регулярка, а не DOMParser: тесты utils крутятся в node, где DOMParser нет, а формат
// ответа плоский и фиксированный. Ссылку берём из первого item — у channel тоже есть
// свой link, на главную 24smi, и он не подходит.
export function extractRssLink(xml) {
    const item = ITEM_RE.exec(xml || "");
    const link = item && LINK_RE.exec(item[1]);

    if (!link) {
        return null;
    }

    let value = link[1].trim();
    const cdata = CDATA_RE.exec(value);

    if (cdata) {
        value = cdata[1].trim();
    } else {
        value = value.replace(/&(amp|lt|gt|quot|apos|#39);/g, (entity) => ENTITIES[entity]);
    }

    // Ответ сторонний: javascript: и относительные адреса в location не пускаем.
    return /^https?:\/\//i.test(value) ? value : null;
}

// Любая неудача — сеть, не-2xx, таймаут, пустой ответ — это null: решение о фолбэке
// принимает вызывающий код.
export async function fetchRssLink({ fetchImpl = fetch, url = REFRESH_RSS_URL, timeoutMs = RSS_TIMEOUT } = {}) {
    const controller = typeof AbortController === "undefined" ? null : new AbortController();
    let timerId = null;

    const timeout = new Promise((resolve) => {
        timerId = setTimeout(() => {
            if (controller) {
                controller.abort();
            }
            resolve(null);
        }, timeoutMs);
    });

    const request = (async () => {
        try {
            const response = await fetchImpl(url, controller ? { signal: controller.signal } : undefined);

            if (!response.ok) {
                return null;
            }

            return extractRssLink(await response.text());
        } catch (error) {
            return null;
        }
    })();

    try {
        return await Promise.race([request, timeout]);
    } finally {
        clearTimeout(timerId);
    }
}

// Время вне вкладки до редиректа: delay кампании (мс) важнее refresh_time потока (сек).
export function redirectDelayMs(timeout, delay) {
    if (delay > 0) {
        return delay;
    }

    return timeout > 0 ? timeout * 1000 : null;
}

export default function BlurReload({
    isDesktop,
    domain,
    refresh,
    useRss = false,
    rssUrl = null,
    delay = null,
    sid,
    timeout,
}) {
    const timeoutIdRef = useRef(null);
    // Номер ухода со вкладки: ответ RSS, пришедший после возврата, относится к
    // старому уходу, и редиректить по нему уже нельзя.
    const attemptRef = useRef(0);
    // selectDeviceVariant пропускает скаляр насквозь: старые кампании хранят
    // refresh строкой, новые — словарём pc/mob, из БД приходит готовый slug.
    const refreshLink = selectDeviceVariant(refresh, isDesktop);

    const waitMs = redirectDelayMs(timeout, delay);

    useEffect(() => {
        if (!waitMs || (!refreshLink && !useRss)) {
            return;
        }

        const params = new URLSearchParams(window.location.search);
        const blur = params.get("blur");

        if (blur != null) {
            return;
        }

        const pageUrl = refreshLink ? `https://${domain}/pages/${refreshLink}/?subid=${sid}` : null;

        const redirect = async () => {
            if (!useRss) {
                window.location.href = pageUrl;
                return;
            }

            const attempt = attemptRef.current;
            // Страница со слагом — запасной путь, когда RSS не ответил.
            const target = (await fetchRssLink({ url: rssUrl || REFRESH_RSS_URL })) || pageUrl;

            if (target && attempt === attemptRef.current) {
                window.location.href = target;
            }
        };

        const onBlur = () => {
            timeoutIdRef.current = setTimeout(redirect, waitMs);
        };

        const onFocus = () => {
            attemptRef.current += 1;

            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
                timeoutIdRef.current = null;
            }
        };

        window.onblur = onBlur;
        window.onfocus = onFocus;

        return () => {
            attemptRef.current += 1;
            window.onblur = null;
            window.onfocus = null;
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
            }
        };
    }, [isDesktop, domain, refreshLink, useRss, rssUrl, waitMs, sid]);

    return null;
}
